/**
 * Monaco Editor Language Configuration for Template JSON
 * 
 * Registers a custom language mode that extends JSON with template syntax support.
 * This allows Monaco to provide syntax highlighting and validation for configuration
 * files that may contain template variables like {{variable}}.
 */

import * as monaco from 'monaco-editor'

// Register the template-json language
monaco.languages.register({ id: 'template-json' })

// Set language configuration (brackets, auto-closing, etc.)
monaco.languages.setLanguageConfiguration('template-json', {
  brackets: [
    ['{', '}'],
    ['[', ']']
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '"', close: '"' }
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '"', close: '"' }
  ]
})

// Set monarch tokenizer (syntax highlighting rules)
monaco.languages.setMonarchTokensProvider('template-json', {
  tokenizer: {
    root: [
      // Template variables like {{variable}}
      [/\{\{[^}]*\}\}/, 'variable'],
      
      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, 'string', '@string'],
      
      // Numbers
      [/-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/, 'number'],
      
      // Keywords
      [/\b(?:true|false|null)\b/, 'keyword'],
      
      // Delimiters and operators
      [/[{}()\[\]]/, '@brackets'],
      [/[,:]/, 'delimiter']
    ],
    
    string: [
      [/[^\\"]+/, 'string'],
      [/\\./, 'string.escape'],
      [/"/, 'string', '@pop']
    ]
  }
})

const templateJsonCompletionProvider: monaco.languages.CompletionItemProvider = {
  provideCompletionItems: (model, position) => {
    const word = model.getWordUntilPosition(position)
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn
    }
    
    return {
      suggestions: [
        {
          label: '{{variable}}',
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: '{{${1:variable}}}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Insert a template variable',
          range: range
        },
        {
          label: '$secret reference',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '"$secret": {\n  "vault": "${1:default}",\n  "ref": "${2:path/to/secret}",\n  "version": "${3:latest}"\n}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Insert a metadata-only secret reference',
          range: range
        },
        {
          label: '_vaults block',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '"_vaults": {\n  "${1:default}": {\n    "provider": "${2:azure-keyvault}",\n    "uri": "${3:https://example.vault.azure.net/}"\n  }\n}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: 'Insert a vault registry block for secret references',
          range: range
        }
      ]
    }
  }
}

// Keep snippet completions available in the legacy template-json mode and the
// built-in JSON modes used across the app so schema validation and snippets coexist.
monaco.languages.registerCompletionItemProvider('template-json', templateJsonCompletionProvider)
monaco.languages.registerCompletionItemProvider('json', templateJsonCompletionProvider)
monaco.languages.registerCompletionItemProvider('jsonc', templateJsonCompletionProvider)

export default {}
